import { gql } from '@apollo/client';

export const QL_UPDATE_ASSET = gql`
  mutation updateAsset($args: UpdateAssetInput) {
    asset {
      update(args: $args) {
        status
        product
        version
        vendor
        name
        id
        type
        classification
        updatedAt
        assetCpe
        assetCpes
        license
      }
    }
  }
`;

//v3
export const GET_ASSET = gql`
  query asset($args: AssetInput) {
    asset(args: $args) {
      id
      status
      updatedAt
      createdAt
      name
      vendor
      product
      version
      assetCpe
      assetCpes
      license
      firstAnalysisTime
      type
      classification
      sha256
      hasRemediation
      assetGroupCount
      assetGroupIds
      quantumCapable
      sizeBytes
      risk {
        score
        category
      }
    }
  }
`;

export const GET_ASSET_WITH_ANALYTICS = gql`
  query assetWithAnalytics($args: AssetInput, $includeLicenseIssues: Boolean = false, $includeAiRelatedDependencies: Boolean = false) {
    asset(args: $args) {
      id
      status
      updatedAt
      createdAt
      name
      vendor
      product
      version
      assetCpe
      assetCpes
      license
      firstAnalysisTime
      type
      classification
      sha256
      hasRemediation
      assetGroupCount
      assetGroupIds
      quantumCapable
      sizeBytes
      risk {
        score
        category
      }
      analytic {
        vulnerability {
          critical
          high
          medium
          low
        }
        exploit {
          threatActor
          ransomware
          botnet
          inTheWild
          knownAttacks
          inKnownExploitedVulnerabilities
          exploitCode
        }
        components {
          application
          framework
          library
          container
          device
          kernel
          package
          kernelModule
          os
          aiModel @include(if: $includeAiRelatedDependencies)
        }
        misconfigurations {
          passed
          failed
          notApplicable
        }
        cryptography {
          publicKey
          privateKey
          certificate
        }
        binaries
        credentials {
          credential
          hash
          crackedCred
          crackedHash
          secrets
        }
        files
        licenseIssues @include(if: $includeLicenseIssues)
        aiComponents @include(if: $includeAiRelatedDependencies)
      }
    }
  }
`;

//v3
export const GET_ASSET_GROUP_ANALYTIC = gql`
  query assetGroupAnalytics($args: AssetGroupAnalyticsInput!) {
    assetGroupAnalytics(args: $args) {
      totalComponents
      exploits
      misconfigurations
      invalidCertificates
      crackedCredentials
    }
  }
`;

export const GET_ASSET_STATUS = gql`
  query assetStatus($args: AssetStatusInput!) {
    assetStatus(args: $args) {
      assetId
      lastUpdatedTime
      hasRunningJob
    }
  }
`;

export const GET_ASSET_UPLOAD = gql`
  query assetUpload($args: AssetUploadInput) {
    assetUpload(args: $args) {
      uploadId
      assetId
      uploaded
    }
  }
`;

export const GET_ASSETS_NAMES = gql`
  query assetsNamesRelay($args: AssetsRelayInput!) {
    assetsRelay(args: $args) {
      edges {
        node {
          assetId: id
          name
          status
          sha256
        }
      }
    }
  }
`;
export const GET_GROUP_ASSETS_NAMES = gql`
  query assetGroupMembers($args: AssetGroupMembersInput!) {
    assetGroupMembers(args: $args) {
      edges {
        node {
          assetId: id
          name
          sha256
          status
        }
      }
    }
  }
`;

/**
 * Paginated rows: `pageInfo` omits `totalCount` so the API skips the expensive count (selection-set driven).
 * Use GET_GROUP_MEMBERS_TOTAL_COUNT when you need the total row count.
 */
export const GET_GROUP_MEMBERS_LIST = gql`
  query assetGroupMembersList($args: AssetGroupMembersInput!) {
    assetGroupMembers(args: $args) {
      edges {
        cursor
        node {
          id
          name
          vendor
          assetGroupCount
          assetGroupIds
          product
          version
          assetCpe
          assetCpes
          license
          createdAt
          updatedAt
          firstAnalysisTime
          type
          classification
          sha256
          risk {
            score
          }
          sizeBytes
          status
          uploadedBy
        }
      }
      pageInfo {
        startCursor
        endCursor
      }
    }
  }
`;

/** Same `AssetGroupMembersInput` as the list; selection includes only `pageInfo { totalCount }`. */
export const GET_GROUP_MEMBERS_TOTAL_COUNT = gql`
  query assetGroupMembersTotalCount($args: AssetGroupMembersInput!) {
    assetGroupMembers(args: $args) {
      pageInfo {
        totalCount
      }
    }
  }
`;

/** Alias of GET_GROUP_MEMBERS_LIST for refetchQueries and existing imports. */
export const GET_GROUP_MEMBERS = GET_GROUP_MEMBERS_LIST;

/**
 * Paginated rows: `pageInfo` omits `totalCount` so the API skips the expensive count (selection-set driven).
 * Use GET_ASSETS_TOTAL_COUNT when you need the total row count.
 */
export const GET_ASSETS_LIST = gql`
  query assetsRelayList($args: AssetsRelayInput!) {
    assetsRelay(args: $args) {
      edges {
        cursor
        node {
          id
          name
          vendor
          assetGroupCount
          assetGroupIds
          product
          firstAnalysisTime
          version
          assetCpe
          assetCpes
          license
          createdAt
          updatedAt
          type
          classification
          sha256
          risk {
            score
          }
          sizeBytes
          status
          uploadedBy
        }
      }
      pageInfo {
        startCursor
        endCursor
      }
    }
  }
`;

/** Same `AssetsRelayInput` as the list; selection includes only `pageInfo { totalCount }`. */
export const GET_ASSETS_TOTAL_COUNT = gql`
  query assetsRelayTotalCount($args: AssetsRelayInput!) {
    assetsRelay(args: $args) {
      pageInfo {
        totalCount
      }
    }
  }
`;

/** Alias of GET_ASSETS_LIST for refetchQueries and existing imports. */
export const GET_ASSETS = GET_ASSETS_LIST;

export const GET_ASSETS_OVERVIEW = gql`
  query assetsOverview($args: AssetOverviewInput!) {
    assetsOverview(args: $args) {
      edges {
        cursor
        node {
          composedAssetId
          name
          vendor
          product
          version
          submitDatetime
          operatingSystem
          operatingSystemKernelVersion
          type
          risk {
            rawScore
            score
            category
          }
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
    }
  }
`;

export const GET_ASSETS_STATUSES = gql`
  query assetsStatuses($args: AssetsRelayInput!) {
    assetsRelay(args: $args) {
      edges {
        node {
          id
          updatedAt
          status
        }
      }
    }
  }
`;

export const GET_GROUP_MEMBER_STATUSES = gql`
  query assetsStatuses($args: AssetGroupMembersInput!) {
    assetGroupMembers(args: $args) {
      edges {
        node {
          id
          updatedAt
          status
        }
      }
    }
  }
`;

export const GET_ALL_ASSETS_IDS = gql`
  query allAssetsIds($args: AssetsRelayInput!) {
    assetsRelay(args: $args) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const LIST_FILES = gql`
  query listFiles($componentId: String!, $filesystemId: ID, $fileInput: FilesInput) {
    asset(args: { assetId: $componentId }) {
      filesystems(args: { filesystemId: $filesystemId }) {
        files(args: $fileInput) {
          filesList {
            file {
              digest {
                md5
                sha1
                sha256
              }
              path
              createdAt
              updatedAt
              size
              mimeType
              permissions
              hasChildren
            }
            fileId
          }
          pageInfo {
            nextPageToken
            totalSize
          }
        }
      }
    }
  }
`;

export const QL_SUBMIT_ASSET = gql`
  mutation submitAsset($fileName: String!, $args: SubmitAssetInput) {
    asset {
      submit(fileName: $fileName, args: $args) {
        uploadUrl
        uploadId
      }
    }
  }
`;

export const GET_SIGNED_URL_DOWNLOAD_FILE = gql`
  query file($args: FileDownloadInput!) {
    download {
      file(args: $args) {
        downloadUrlsList
        pathsList
      }
    }
  }
`;

export const GET_SIGNED_URL_DOWNLOAD_FILE_LIST = gql`
  query fileList($args: FileListDownloadInput!) {
    download {
      fileList(args: $args) {
        downloadUrlsList
        pathsList
      }
    }
  }
`;

export const GET_SIGNED_URL_DOWNLOAD_EXTRACTED_COMPONENT = gql`
  query extractedFirmware($args: ExtractedFirmwareDownloadInput!) {
    download {
      extractedFirmware(args: $args) {
        downloadUrlsList
        pathsList
      }
    }
  }
`;

export const GET_SIGNED_URL_DOWNLOAD_COMPONENT = gql`
  query firmware($args: FirmwareDownloadInput!) {
    download {
      firmware(args: $args) {
        downloadUrlsList
        pathsList
      }
    }
  }
`;

export const SIFT_SEARCH = gql`
  query sift($args: SiftInput!) {
    sift {
      files(args: $args) {
        hasMore
        files {
          sha256
          path
          matches {
            file
            ordinal
            similarity
            text
          }
          assets {
            componentId
            name
            product
            packages
          }
        }
      }
    }
  }
`;

export const SIFT_METRICS = gql`
  query siftCount {
    sift {
      count {
        assetCount
        fileCount
        pythonFileCount
        scriptFileCount
        textFileCount
      }
    }
  }
`;

const ACTIVITY_ENTITY_FRAGMENT = gql`
  fragment ActivityEntityFields on ActivityEntity {
    __typename
    ... on AssetCreatedPayload {
      assetType
      name
      vendor
      version
    }
    ... on AssetChangedPayload {
      assetType
      name
      vendor
      version
    }
    ... on AssetRiskChangedPayload {
      assetType
      name
      vendor
      version
      oldRisk {
        score
        category
      }
      newRisk {
        score
        category
      }
    }
    ... on IdentificationAddedPayload {
      identificationId
      name
      vendor
      version
      remediationAction
    }
    ... on IdentificationRemovedPayload {
      identificationId
      name
      vendor
      version
      remediationAction
    }
    ... on VulnerabilityAddedPayload {
      cve
      identificationId
      entity {
        severity
        epssScore
        cvssScore
        knownExploited
        exploitMaturity
      }
    }
    ... on VulnerabilityRemediatedPayload {
      vulnerabilityId
      cve
      severity
      componentName
      componentVersion
      componentVendor
      vexJustification
      vexStatus
      description
    }
    ... on VulnerabilityUpdatedPayload {
      cve
      identificationId
      vulnerabilityUpdates {
        field
        previous
        current
      }
    }
    ... on VulnerabilityRemovedPayload {
      cve
      identificationId
      entity {
        severity
        epssScore
        cvssScore
        knownExploited
        exploitMaturity
      }
    }
    ... on MisconfigChangedPayload {
      checkId
      checkName
      oldStatus
      newStatus
    }
    ... on AssetGroupAddedPayload {
      groupId
      name
    }
    ... on AssetGroupRemovedPayload {
      groupId
      name
    }
    ... on SecretRemediatedPayload {
      secretId
      subtype
      type
      category
      filePath
      oldStatus
      newStatus
      detail
      user
    }
    ... on LicenseIssueRemediatedPayload {
      issueName
      componentId
      componentVersion
      componentName
      entityUpdates {
        field
        previous
        current
      }
    }
    ... on AssetAnalysisCompletedPayload {
      assetId
      revision
      failure
    }
    ... on GroupCreatedPayload {
      groupId
      name
      description
    }
    ... on GroupUpdatedPayload {
      groupId
      name
      description
    }
    ... on GroupDeletedPayload {
      groupId
      name
    }
    ... on GroupMembershipChangedPayload {
      groupId
      name
      membersAdded
      membersRemoved
    }
    ... on OrgSettingsUpdatedPayload {
      _empty
    }
    ... on UnspecifiedPayload {
      payloadType
      rawData
    }
  }
`;

export const GET_ACTIVITY = gql`
  ${ACTIVITY_ENTITY_FRAGMENT}
  query activity($args: ActivityInput!) {
    activity(args: $args) {
      edges {
        cursor
        node {
          id
          correlationId
          createdAt
          orgId
          orgName
          assetId
          assetName
          entityType
          activityType
          user
          description
          entity {
            ...ActivityEntityFields
            __typename
          }
          __typename
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

/** Same `ActivityInput` as the list; selection includes only `pageInfo { totalCount }`. */
export const GET_ACTIVITY_TOTAL_COUNT = gql`
  query activityTotalCount($args: ActivityInput!) {
    activity(args: $args) {
      pageInfo {
        totalCount
      }
    }
  }
`;
